import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowLeft } from "react-feather";
import toast from "react-hot-toast";
import { useCreateStockInMutation, useListLposQuery, useGetLpoQuery } from "../../stockApi";
import { useGetBranchesQuery } from "../../../admin/adminApi";
import { useClaims } from "../../../auth/useClaims";
import { useCanCreateStockIn, useCanCreateDirectStockIn } from "../../useStockPermissions";
import PartyPicker from "../../components/PartyPicker";
import LineItemsEditor, { emptyRow, computeTotals, computeLineAmount } from "../../components/LineItemsEditor";
import { useFillMissingUnits } from "../../components/lineUnits";
import StockVatTotals, { vatRateLabel } from "../../components/StockVatTotals";
import { DEFAULT_VAT_PERCENT } from "../../../../components/common/vatDefaults";
import { paths } from "../../../../routes/paths";
import { useDefaultFormBranch } from "../../../branchFilter/useDefaultFormBranch";

// VAT per line (2026-09-14): an LPO line keeps the LPO's own VAT %, a direct
// line starts at the product's VAT (16% default). Rounded per line, same as
// the API stores it (Batch172).
const columns = { showRate: true, showVat: true, showLineTotal: true, roundPerLine: true, showRemarks: false, showReason: false, showMaxQty: true };
const newRow = () => ({ ...emptyRow(), vatPercent: String(DEFAULT_VAT_PERCENT) });
const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

// Stock-In (GRN) - either against an approved LPO (lines prefill with the
// remaining orderedQty-receivedQty, capped there) or fully direct/ad-hoc
// (needs the separate Stock.CreateDirectStockIn permission - kept apart
// from Stock.CreateStockIn so it can't silently bypass the LPO approval
// gate). Arrives either blank (from the Stock-In list) or prefilled via
// location.state.fromLpo (the "Create Stock-In" shortcut on LpoDetail.jsx).
const StockInForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromLpo = location.state?.fromLpo;

  const canCreate = useCanCreateStockIn();
  const canDirect = useCanCreateDirectStockIn();
  const { branches: callerBranchCodes, isAllBranches } = useClaims();
  const { data: branches = [] } = useGetBranchesQuery();
  const [createStockIn, { isLoading: isSaving }] = useCreateStockInMutation();

  const availableBranches = isAllBranches
    ? branches
    : branches.filter((b) => callerBranchCodes?.includes(b.branchCode));

  const defaultBranchId = useDefaultFormBranch(availableBranches);
  const [branchId, setBranchId] = useState(defaultBranchId);
  // A new record starts on the header branch (useDefaultFormBranch, 2026-10-02); the
  // branch list loads async, so it is filled in once known unless already chosen.
  useEffect(() => {
    if (!branchId && defaultBranchId !== "") setBranchId(defaultBranchId);
  }, [defaultBranchId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [mode, setMode] = useState("lpo"); // "lpo" | "direct"
  const [selectedLpoGuid, setSelectedLpoGuid] = useState(fromLpo?.recordGuid || "");
  const [supplierOption, setSupplierOption] = useState(null);
  const [invoiceRef, setInvoiceRef] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [stockInDate, setStockInDate] = useState(toDateInput(new Date()));
  const [remarks, setRemarks] = useState("");
  const [lines, setLines] = useState([newRow()]);
  const [seededFromLpo, setSeededFromLpo] = useState(false);

  /*
    The LPO dropdown (2026-09-18 fixes).

    Two separate faults made this render as an empty "--Select an approved LPO--"
    with no explanation, which is how it was reported ("not able to select lpo"):

      1. GET /stock/lpos was gated on Stock.ViewLpo alone, a permission distinct
         from the Stock.CreateStockIn this very form requires. A storekeeper with
         create-but-not-view passed the form's own check, saw an enabled form, and
         got a 403 nothing here ever inspected. Fixed server-side by widening that
         policy; `lpoError` below makes any remaining failure visible rather than
         silently empty.

      2. The request asked for the 200 most recent LPOs of ANY status and then
         filtered to APPROVED/PARTIALLY_RECEIVED in the browser - so in a busy
         branch an older approved-but-unreceived LPO fell outside the window and
         became unreachable, with no search and no paging. Now asks the server for
         each receivable status directly (the proc takes one @Status, hence two
         calls) so the cap applies per status instead of across all of them.
  */
  const { data: approvedLpoPage, error: approvedLpoError } = useListLposQuery(
    { branchId: branchId ? Number(branchId) : undefined, status: "APPROVED", pageSize: 200 },
    { skip: !branchId || mode !== "lpo" }
  );
  const { data: partialLpoPage, error: partialLpoError } = useListLposQuery(
    { branchId: branchId ? Number(branchId) : undefined, status: "PARTIALLY_RECEIVED", pageSize: 200 },
    { skip: !branchId || mode !== "lpo" }
  );

  const lpoError = approvedLpoError || partialLpoError;
  const openLpos = [...(approvedLpoPage?.items || []), ...(partialLpoPage?.items || [])];

  const { data: selectedLpo } = useGetLpoQuery(selectedLpoGuid, { skip: !selectedLpoGuid });
  const fillMissingUnits = useFillMissingUnits();

  useEffect(() => {
    if (!selectedLpo) return;
    setBranchId(selectedLpo.branchId);
    setSupplierOption({
      value: selectedLpo.supplierId,
      label: selectedLpo.supplierName,
      customer: selectedLpo.supplierId != null
        ? { custId: selectedLpo.supplierId, branchId: selectedLpo.supplierBranchId, custName: selectedLpo.supplierName }
        : null,
    });
    if (!seededFromLpo || fromLpo) {
      const lpoRows = selectedLpo.lines
        .filter((l) => Number(l.orderedQty) - Number(l.receivedQty) > 0)
        .map((l) => ({
          ...emptyRow(),
          productCode: l.productCode,
          productName: l.productName,
          unit: l.unit || "",
          qty: Number(l.orderedQty) - Number(l.receivedQty),
          rate: l.unitRate,
          vatPercent: String(l.vatPercent ?? 0),
          maxQty: Number(l.orderedQty) - Number(l.receivedQty),
          locked: true,
          refId: l.lineId,
        }));
      setLines(lpoRows);
      // These rows are locked, so the picker's own auto-fill can't run - an
      // LPO line saved without a unit would stay blank all the way onto the
      // GRN. Resolve it from the Product Master instead.
      fillMissingUnits(lpoRows, selectedLpo.branchId).then((filled) => {
        if (filled !== lpoRows) setLines(filled);
      });
      setSeededFromLpo(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLpo]);

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setSelectedLpoGuid("");
    setSupplierOption(null);
    setSeededFromLpo(false);
    setLines([newRow()]);
  };

  const totals = useMemo(() => computeTotals(lines, columns), [lines]);
  const readOnly = mode === "direct" ? !canDirect : !canCreate;

  const handleSave = async () => {
    if (!branchId) return toast.error("Branch is required");
    const supplierName = (supplierOption?.customer?.custName || supplierOption?.label || "").trim();
    if (!supplierName) return toast.error("Supplier name is required");
    const validLines = lines.filter((l) => l.productCode && Number(l.qty) > 0);
    if (validLines.length === 0) return toast.error("Add at least one line with a product and quantity");

    const body = {
      branchId: Number(branchId),
      lpoRecordGuid: mode === "lpo" ? selectedLpoGuid || null : null,
      supplierId: supplierOption?.customer?.custId ?? null,
      supplierBranchId: supplierOption?.customer?.branchId ?? null,
      supplierName,
      invoiceRef: invoiceRef.trim() || null,
      invoiceDate: invoiceDate ? new Date(invoiceDate).toISOString() : null,
      stockInDate: new Date(stockInDate).toISOString(),
      // Server recomputes all amounts from qty/rate/VAT % - sent for completeness.
      totalAmount: totals.subTotal,
      remarks: remarks.trim() || null,
      lines: validLines.map((l) => ({
        lpoLineId: l.refId || null,
        productCode: Number(l.productCode),
        productName: l.productName,
        receivedQty: Number(l.qty),
        unit: l.unit || null,
        unitRate: Number(l.rate) || 0,
        lineAmount: computeLineAmount(l, columns).base,
        vatPercent: Number(l.vatPercent) || 0,
      })),
    };

    try {
      const created = await createStockIn(body).unwrap();
      toast.success("Stock-In posted");
      navigate(paths.stockInDetail(created.recordGuid), { replace: true });
    } catch (error) {
      toast.error(error?.message || "Could not post the Stock-In");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.stockIn} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to Stock-In
              </Link>
              <h4>New Stock-In (GRN)</h4>
            </div>
          </div>
        </div>

        {readOnly && (
          <div className="alert alert-info">
            You have read-only access. Posting a {mode === "direct" ? "direct" : "LPO-linked"} Stock-In requires
            {" "}Stock.{mode === "direct" ? "CreateDirectStockIn" : "CreateStockIn"}.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <div className="d-flex gap-4 mb-3">
              <div className="form-check">
                <input type="radio" className="form-check-input" id="mode-lpo" checked={mode === "lpo"} onChange={() => handleModeChange("lpo")} />
                <label className="form-check-label" htmlFor="mode-lpo">Against an LPO</label>
              </div>
              {canDirect && (
                <div className="form-check">
                  <input type="radio" className="form-check-input" id="mode-direct" checked={mode === "direct"} onChange={() => handleModeChange("direct")} />
                  <label className="form-check-label" htmlFor="mode-direct">Direct / Ad-hoc (no LPO)</label>
                </div>
              )}
            </div>

            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Branch</label>
                <select className="form-control" value={branchId} onChange={(e) => { setBranchId(e.target.value); setSelectedLpoGuid(""); }} disabled={readOnly || (mode === "lpo" && Boolean(selectedLpoGuid))}>
                  <option value="">--Select--</option>
                  {availableBranches.map((b) => (
                    <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                  ))}
                </select>
                {/* A branchless account (no branch rows, and not all-branches) gets an
                    empty list here, which then disables the LPO dropdown too - the
                    second way this form silently dead-ends. Name the cause. */}
                {branches.length > 0 && availableBranches.length === 0 && (
                  <div className="text-danger small mt-1">
                    Your account has no branch assigned, so there is nothing to receive into. Ask an administrator to assign your branch.
                  </div>
                )}
              </div>

              {mode === "lpo" ? (
                <div className="col-md-5 mb-3">
                  <label className="form-label">LPO</label>
                  <select
                    className="form-control"
                    value={selectedLpoGuid}
                    onChange={(e) => { setSelectedLpoGuid(e.target.value); setSeededFromLpo(false); }}
                    disabled={readOnly || !branchId}
                  >
                    <option value="">--Select an approved LPO--</option>
                    {openLpos.map((l) => (
                      <option key={l.recordGuid} value={l.recordGuid}>{l.lpoNumber} - {l.supplierName}</option>
                    ))}
                  </select>
                  {/* An empty dropdown used to be indistinguishable from a failed
                      request. Say which it is - "there are none" and "you were
                      refused" need very different next steps from the user. */}
                  {lpoError && (
                    <div className="text-danger small mt-1">
                      Could not load this branch&apos;s LPOs ({lpoError?.message || "server error"}).
                    </div>
                  )}
                  {!lpoError && branchId && openLpos.length === 0 && (
                    <div className="text-muted small mt-1">
                      This branch has no approved LPO waiting to be received. Use Direct / Ad-hoc if there is no LPO.
                    </div>
                  )}
                </div>
              ) : (
                <div className="col-md-5 mb-3">
                  <label className="form-label">Supplier / Party (Customer Master)</label>
                  <PartyPicker mode="all-branches" value={supplierOption} onChange={setSupplierOption} disabled={readOnly} creatable />
                </div>
              )}

              <div className="col-md-2 mb-3">
                <label className="form-label">Invoice Ref</label>
                <input type="text" className="form-control" value={invoiceRef} onChange={(e) => setInvoiceRef(e.target.value)} disabled={readOnly} />
              </div>
              <div className="col-md-2 mb-3">
                <label className="form-label">Invoice Date</label>
                <input type="date" className="form-control" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} disabled={readOnly} />
              </div>
              <div className="col-md-2 mb-3">
                <label className="form-label">Stock-In Date</label>
                <input type="date" className="form-control" value={stockInDate} onChange={(e) => setStockInDate(e.target.value)} disabled={readOnly} />
              </div>
            </div>

            <hr />
            <h5 className="mb-3">Received Lines</h5>
            <LineItemsEditor
              branchId={branchId ? Number(branchId) : undefined}
              rows={lines}
              onChange={setLines}
              columns={columns}
              disabled={readOnly || (mode === "lpo" && !selectedLpoGuid)}
            />

            <div className="row mt-3">
              <div className="col-md-8 mb-3">
                <label className="form-label">Remarks</label>
                <textarea className="form-control" rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3 d-flex align-items-end justify-content-end">
                <StockVatTotals subTotal={totals.subTotal} vatAmount={totals.vatAmount} grandTotal={totals.netAmount} vatRate={vatRateLabel(lines)} />
              </div>
            </div>

            {!readOnly && (
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>Save</button>
                <button type="button" className="btn btn-secondary" onClick={() => navigate(paths.stockIn)}>Cancel</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockInForm;
